import { useCallback, useState } from "react"
import { useSelector } from "react-redux"
import { useCollapsedLocation } from "./hooks/useCollapsedLocation"
import UnknownLocationWearableCard from "./UnknownLocationWearableCard"
import {
   getWearableCardPlaceholderClassName,
   getWearableCardSize,
   gridSettings,
   UNKNOWN_LOCATION_ID,
} from "./utils"
import Grid from "@/components/shared/Grid"
import { Icon, IconColor } from "@/components/shared/Icon"
import LazyMount from "@/components/shared/LazyMount"
import { TestId } from "@/constants/globals"
import { useScreenWidth } from "@/hooks/useScreenWidth"
import { selectUnknownLocationWearableIds } from "@/store/unknownLocationWearablesSlice"

const WIDTH_SCREEN = 1200

interface UnknownLocationSectionProps {
   onSwapDeviceClick?: (wearableId: string) => void
   onNavigateToInmateDetail?: (inmateId: string) => void
}

const UnknownLocationSection = ({
   onSwapDeviceClick,
   onNavigateToInmateDetail,
}: UnknownLocationSectionProps) => {
   const wearableIds = useSelector(selectUnknownLocationWearableIds)
   const width = useScreenWidth()
   const smallScreen = width <= WIDTH_SCREEN
   const wearableCardSize = getWearableCardSize()
   const { isCollapsed, toggleCollapsed } =
      useCollapsedLocation(UNKNOWN_LOCATION_ID)
   const [expandedWearableIds, setExpandedWearableIds] = useState<Set<string>>(
      new Set()
   )

   const handleWearableExpandedChange = useCallback(
      (wearableId: string, isExpanded: boolean) => {
         setExpandedWearableIds((prev) => {
            const next = new Set(prev)
            if (isExpanded) next.add(wearableId)
            else next.delete(wearableId)
            return next
         })
      },
      []
   )

   if (!wearableIds.length) {
      return null
   }

   return (
      <div className="floor-overview__location-group floor-overview__unknown-location">
         <span
            className="floor-overview__location-label"
            data-testid={TestId.FLOOR_OVERVIEW_UNKNOWN_LOCATION_LABEL}
            onClick={toggleCollapsed}
         >
            <Icon
               name={isCollapsed ? "arrowRight" : "arrowDown"}
               size="16"
               color={IconColor.COOL_GRAY}
            />
            <Icon
               name="unknownLocation"
               color={IconColor.JET_BLACK}
            />
            Unknown Location
            {isCollapsed && (
               <span
                  className="floor-overview__location-total-count"
                  data-testid={
                     TestId.FLOOR_OVERVIEW_UNKNOWN_LOCATION_TOTAL_COUNT
                  }
               >
                  <Icon
                     name="smartwatch"
                     size="24"
                     color={IconColor.HONOLULU_BLUE}
                  />
                  <span className="floor-overview__location-total-count-value">
                     {wearableIds.length}
                  </span>
               </span>
            )}
         </span>
         {!isCollapsed && (
            <Grid sx={smallScreen ? gridSettings.small : gridSettings.default}>
               {wearableIds.map((wearableId) => (
                  <LazyMount
                     key={`unknown-location-wearable-${wearableId}`}
                     className={getWearableCardPlaceholderClassName(
                        wearableCardSize,
                        expandedWearableIds.has(wearableId)
                     )}
                  >
                     <UnknownLocationWearableCard
                        wearableId={wearableId}
                        onSwapDeviceClick={onSwapDeviceClick}
                        onNavigateToInmateDetail={onNavigateToInmateDetail}
                        isExpanded={expandedWearableIds.has(wearableId)}
                        onExpandedChange={handleWearableExpandedChange}
                     />
                  </LazyMount>
               ))}
            </Grid>
         )}
      </div>
   )
}

export default UnknownLocationSection
